// Хук для прямого применения текстуры к конкретным mesh объектам
import { useEffect } from 'react';
import * as THREE from 'three';
import {
  applyTextureToMesh,
  getWallOrientation,
  getTextureUrlFromFinishing as getTextureUrl,
  normalizeTextureOptions,
  createTextureVariant
} from '../utils/textureUtils';
import { useMaterialManager } from './useMaterialManager';

export const getTextureUrlFromFinishing = getTextureUrl;

/**
 * Применяет текстуру напрямую к выбранным mesh объектам в сцене
 * @param {Object} scene - 3D сцена модели бассейна
 * @param {string|null} textureUrl - URL текстуры для применения
 * @param {Object} rawOptions - Опции применения текстуры
 */
export const useDirectTextureApplication = (scene, textureUrl, rawOptions = {}) => {
  const { saveMaterial, restoreMaterials } = useMaterialManager(scene);

  const options = normalizeTextureOptions(rawOptions);

  useEffect(() => {
    if (!scene) return;

    if (!textureUrl) {
      restoreMaterials();
      return;
    }

    let isCurrent = true;

    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(
      textureUrl,
      (texture) => {
        if (!isCurrent) return;

        // Базовая настройка текстуры
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.flipY = false;
        texture.generateMipmaps = true;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.anisotropy = 16;

        // 1. Создание вариантов текстур
        const wallTextureLength = createTextureVariant(texture,
          options.swapWallUV ? options.wallRepeatHeight : options.wallRepeatLength,
          options.swapWallUV ? options.wallRepeatLength : options.wallRepeatHeight
        );

        const wallTextureWidth = createTextureVariant(texture,
          options.swapWallUV ? options.wallRepeatHeight : options.wallRepeatWidth,
          options.swapWallUV ? options.wallRepeatWidth : options.wallRepeatHeight
        );

        const floorTexture = createTextureVariant(texture,
          options.swapFloorUV ? options.floorRepeatWidth : options.floorRepeatLength,
          options.swapFloorUV ? options.floorRepeatLength : options.floorRepeatWidth
        );

        // 2. Сбор всех Mesh объектов
        const allMeshes = [];
        let meshIndex = 0;
        scene.traverse((child) => {
          if (child.isMesh) {
            allMeshes.push({
              index: meshIndex,
              name: child.name || 'unnamed',
              uuid: child.uuid,
              object: child
            });
            meshIndex++;
          }
        });

        const applyByIndex = (targetIndex, textureVariant, extraOpts) => {
          const target = allMeshes.find(m => m.index === targetIndex);
          if (target) {
            applyTextureToMesh(target.object, textureVariant, { ...options, ...extraOpts }, saveMaterial);
            return true;
          }
          return false;
        };

        // 3. Применение текстур по приоритетам
        let floorApplied = false;
        let wallWidthApplied = false;
        let wallLengthApplied = false;

        if (typeof options.floorMeshIndex === 'number') {
          floorApplied = applyByIndex(options.floorMeshIndex, floorTexture, { swapFloorUV: options.swapFloorUV });
        }

        const widthIndex = options.wallWidthMeshIndex ?? options.wallMeshIndex;
        if (typeof widthIndex === 'number') {
          wallWidthApplied = applyByIndex(widthIndex, wallTextureWidth, {
            wallOrientation: 'width',
            wallWidthScale: options.wallWidthScale
          });
        }

        if (typeof options.wallLengthMeshIndex === 'number') {
          wallLengthApplied = applyByIndex(options.wallLengthMeshIndex, wallTextureLength, {
            wallOrientation: 'length',
            wallLengthScale: options.wallLengthScale
          });
        }

        if (!floorApplied || !wallWidthApplied || !wallLengthApplied) {
          allMeshes.forEach(({ object: child, name, index }) => {
            if (child.userData?.__hasFinishingTexture) return;

            if (!floorApplied) {
              const matchesFloor = (options.targetFloorMeshName && name.toLowerCase().includes(options.targetFloorMeshName.toLowerCase())) ||
                (!options.targetFloorMeshName && (name.toLowerCase().includes('floor') || name.toLowerCase().includes('bottom') || index === 0));

              if (matchesFloor) {
                applyTextureToMesh(child, floorTexture, { ...options, swapUV: options.swapFloorUV }, saveMaterial);
                floorApplied = true;
                return;
              }
            }

            const orientation = getWallOrientation(child);

            if (!wallWidthApplied) {
              const isWidthWall = orientation === 'width' || (!orientation && !wallLengthApplied);
              if (isWidthWall) {
                applyTextureToMesh(child, wallTextureWidth, { ...options, wallOrientation: 'width', swapUV: options.swapWallUV }, saveMaterial);
                wallWidthApplied = true;
                return;
              }
            }

            if (!wallLengthApplied) {
              const isLengthWall = orientation === 'length' || (!orientation && wallWidthApplied);
              if (isLengthWall) {
                applyTextureToMesh(child, wallTextureLength, { ...options, wallOrientation: 'length', swapUV: options.swapWallUV }, saveMaterial);
                wallLengthApplied = true;
              }
            }
          });
        }
      },
      undefined,
      (err) => process.env.NODE_ENV === 'development' && console.error('Error loading texture:', err)
    );

    return () => {
      isCurrent = false;
      restoreMaterials();
    };
  }, [scene, textureUrl, JSON.stringify(options)]);
};
